/*
 * HomeFixer360 - GDPR Compliance
 * Copyright (c) 2025 Guy T. Dunn Sr. / Idea Guy Brands Incorporated
 * All Rights Reserved.
 * 
 * This software is proprietary and confidential. Unauthorized copying, distribution,
 * modification, or use is strictly prohibited.
 * 
 * For licensing inquiries: guy@ideaguybrands.com
 */

import React, { useState } from 'react';
import { ArrowLeft, Shield, Eye, Download, Trash2, Edit, Globe, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const GDPRCompliance = () => {
  const [requestType, setRequestType] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    fullName: '',
    requestDetails: '',
    verificationMethod: 'email'
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmitRequest = () => {
    // Handle GDPR request submission
    alert('GDPR request submitted. We will respond within 30 days.');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="flex items-center space-x-4">
            <Button 
              variant="outline" 
              onClick={() => window.history.back()}
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back</span>
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">GDPR Compliance</h1>
              <p className="text-gray-600">Your rights under the General Data Protection Regulation</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-lg shadow-sm border p-8">
          
          {/* GDPR Banner */}
          <div className="mb-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center space-x-2">
              <Globe className="w-5 h-5 text-blue-600" />
              <span className="font-semibold text-blue-900">GDPR Rights Protected</span>
            </div>
            <p className="text-blue-700 mt-2">
              HomeFixer360 is committed to protecting your privacy rights under the European General Data Protection Regulation (GDPR).
            </p>
          </div>

          {/* Introduction */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
              <Shield className="w-6 h-6 text-green-600 mr-3" />
              Your Data Protection Rights
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Under the GDPR, individuals in the European Union have specific rights regarding their personal data. 
              Idea Guy Brands Incorporated respects these rights and provides mechanisms to exercise them through 
              HomeFixer360.
            </p>
            <p className="text-gray-700 leading-relaxed">
              This page explains your rights, how to exercise them, and our obligations in protecting your personal data.
            </p>
          </section>

          {/* Your Rights */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Your GDPR Rights</h2>
            
            <div className="grid gap-6">
              {/* Right to Access */}
              <div className="border rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start space-x-4">
                  <Eye className="w-8 h-8 text-blue-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Right to Access (Article 15)</h3>
                    <p className="text-gray-700 mb-3">
                      You have the right to obtain confirmation that we are processing your personal data and to access that data.
                    </p>
                    <ul className="list-disc pl-4 space-y-1 text-gray-600 text-sm">
                      <li>What personal data we collect and process</li>
                      <li>Why we process your data and the legal basis</li>
                      <li>Who we share your data with</li>
                      <li>How long we keep your data</li>
                      <li>Your other rights under GDPR</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Right to Rectification */}
              <div className="border rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start space-x-4">
                  <Edit className="w-8 h-8 text-green-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Right to Rectification (Article 16)</h3>
                    <p className="text-gray-700 mb-3">
                      You have the right to have inaccurate personal data corrected and incomplete data completed.
                    </p>
                    <ul className="list-disc pl-4 space-y-1 text-gray-600 text-sm">
                      <li>Update your account information</li>
                      <li>Correct billing or contact details</li>
                      <li>Modify company information and preferences</li>
                      <li>Fix errors in project or customer data</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Right to Erasure */}
              <div className="border rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start space-x-4">
                  <Trash2 className="w-8 h-8 text-red-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Right to Erasure (Article 17)</h3>
                    <p className="text-gray-700 mb-3">
                      Also known as the "right to be forgotten," you can request deletion of your personal data under certain circumstances.
                    </p>
                    <ul className="list-disc pl-4 space-y-1 text-gray-600 text-sm">
                      <li>Data is no longer necessary for the original purpose</li>
                      <li>You withdraw consent and no other legal basis exists</li>
                      <li>Data has been unlawfully processed</li>
                      <li>Deletion is required for legal compliance</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Right to Data Portability */}
              <div className="border rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start space-x-4">
                  <Download className="w-8 h-8 text-purple-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Right to Data Portability (Article 20)</h3>
                    <p className="text-gray-700 mb-3">
                      You have the right to receive your personal data in a structured, commonly used format and to transmit it to another controller.
                    </p>
                    <ul className="list-disc pl-4 space-y-1 text-gray-600 text-sm">
                      <li>Export your account and project data</li>
                      <li>Download customer and measurement records</li>
                      <li>Transfer data to competing platforms</li>
                      <li>Receive data in JSON or CSV format</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Right to Object */}
              <div className="border rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start space-x-4">
                  <AlertCircle className="w-8 h-8 text-orange-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Right to Object (Article 21)</h3>
                    <p className="text-gray-700 mb-3">
                      You have the right to object to certain types of data processing, including direct marketing and profiling.
                    </p>
                    <ul className="list-disc pl-4 space-y-1 text-gray-600 text-sm">
                      <li>Object to marketing communications</li>
                      <li>Stop automated decision-making processes</li>
                      <li>Opt out of analytics and tracking</li>
                      <li>Restrict processing for specific purposes</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Request Form */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Exercise Your Rights</h2>
            <p className="text-gray-700 mb-6">
              Use the form below to submit a GDPR request. We will respond within 30 days and may require 
              identity verification to protect your privacy.
            </p>

            <div className="border rounded-lg p-6 bg-gray-50">
              <div className="space-y-6">
                {/* Request Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Type of Request <span className="text-red-500">*</span>
                  </label>
                  <div className="grid md:grid-cols-2 gap-3">
                    {[
                      { value: 'access', label: 'Access My Data', icon: Eye },
                      { value: 'rectify', label: 'Correct My Data', icon: Edit },
                      { value: 'delete', label: 'Delete My Data', icon: Trash2 },
                      { value: 'export', label: 'Export My Data', icon: Download },
                      { value: 'object', label: 'Object to Processing', icon: AlertCircle },
                      { value: 'other', label: 'Other Request', icon: CheckCircle }
                    ].map(({ value, label, icon: Icon }) => (
                      <label key={value} className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-white transition-colors">
                        <input
                          type="radio"
                          name="requestType"
                          value={value}
                          checked={requestType === value}
                          onChange={(e) => setRequestType(e.target.value)}
                          className="text-blue-600 focus:ring-blue-500"
                        />
                        <Icon className="w-5 h-5 text-gray-600" />
                        <span className="text-sm font-medium text-gray-900">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Personal Information */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.fullName}
                      onChange={(e) => handleInputChange('fullName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Your email address"
                    />
                  </div>
                </div>

                {/* Request Details */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Request Details
                  </label>
                  <textarea
                    value={formData.requestDetails}
                    onChange={(e) => handleInputChange('requestDetails', e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Please provide additional details about your request..."
                  />
                </div>

                {/* Verification Method */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Identity Verification Method <span className="text-red-500">*</span>
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-3">
                      <input
                        type="radio"
                        name="verification"
                        value="email"
                        checked={formData.verificationMethod === 'email'}
                        onChange={(e) => handleInputChange('verificationMethod', e.target.value)}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Email verification (we'll send a confirmation link)</span>
                    </label>
                    <label className="flex items-center space-x-3">
                      <input
                        type="radio"
                        name="verification"
                        value="account"
                        checked={formData.verificationMethod === 'account'}
                        onChange={(e) => handleInputChange('verificationMethod', e.target.value)}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Account login verification (log in to verify identity)</span>
                    </label>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-4 border-t">
                  <Button 
                    onClick={handleSubmitRequest}
                    disabled={!requestType || !formData.email || !formData.fullName}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3"
                  >
                    Submit GDPR Request
                  </Button>
                  <p className="text-xs text-gray-600 mt-2">
                    We will respond to your request within 30 days as required by GDPR Article 12.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Legal Basis */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Legal Basis for Processing</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-gray-200 rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="border-b px-4 py-3 text-left font-medium">Data Category</th>
                    <th className="border-b px-4 py-3 text-left font-medium">Legal Basis</th>
                    <th className="border-b px-4 py-3 text-left font-medium">Purpose</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border-b px-4 py-3">Account Information</td>
                    <td className="border-b px-4 py-3">Contract Performance</td>
                    <td className="border-b px-4 py-3">Provide HomeFixer360 services</td>
                  </tr>
                  <tr>
                    <td className="border-b px-4 py-3">Payment Data</td>
                    <td className="border-b px-4 py-3">Contract Performance</td>
                    <td className="border-b px-4 py-3">Process subscription payments</td>
                  </tr>
                  <tr>
                    <td className="border-b px-4 py-3">Usage Analytics</td>
                    <td className="border-b px-4 py-3">Legitimate Interest</td>
                    <td className="border-b px-4 py-3">Improve platform performance</td>
                  </tr>
                  <tr>
                    <td className="border-b px-4 py-3">Marketing Communications</td>
                    <td className="border-b px-4 py-3">Consent</td>
                    <td className="border-b px-4 py-3">Send promotional content</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3">Legal Compliance Data</td>
                    <td className="px-4 py-3">Legal Obligation</td>
                    <td className="px-4 py-3">Meet regulatory requirements</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Data Transfers */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">International Data Transfers</h2>
            <p className="text-gray-700 mb-4">
              HomeFixer360 may transfer your personal data outside the European Economic Area (EEA) to provide our services. 
              When we do so, we ensure appropriate safeguards are in place:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Adequacy Decisions:</strong> Transfers to countries with adequate data protection (e.g., Canada, Japan)</li>
              <li><strong>Standard Contractual Clauses:</strong> EU-approved contract terms for data protection</li>
              <li><strong>Certification Schemes:</strong> Transfers to certified organizations under recognized frameworks</li>
              <li><strong>Binding Corporate Rules:</strong> Internal policies for multinational organizations</li>
            </ul>
          </section>

          {/* Response Times */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Response Times and Fees</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-800 mb-3">Standard Response Times</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li><strong>Initial Response:</strong> Within 5 business days</li>
                  <li><strong>Complete Response:</strong> Within 30 days maximum</li>
                  <li><strong>Complex Requests:</strong> May extend by 60 days (with notification)</li>
                  <li><strong>Emergency Requests:</strong> Within 72 hours (data breaches)</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 mb-3">Fees and Charges</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li><strong>First Request:</strong> No charge</li>
                  <li><strong>Additional Requests:</strong> May incur reasonable fees</li>
                  <li><strong>Excessive Requests:</strong> Administrative fees may apply</li>
                  <li><strong>Data Export:</strong> No charge for standard formats</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Complaints */}
          <section className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Right to Lodge a Complaint</h2>
            <p className="text-gray-700 mb-4">
              If you believe we have not handled your personal data properly, you have the right to lodge a complaint 
              with a supervisory authority, particularly in the EU member state where you live, work, or where the 
              alleged infringement occurred.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-blue-900 font-medium mb-2">Before filing a complaint with authorities, please contact us:</p>
              <div className="text-blue-800 text-sm space-y-1">
                <p><strong>Data Protection Officer:</strong> Guy T. Dunn Sr.</p>
                <p><strong>Email:</strong> guy@ideaguybrands.com</p>
                <p><strong>Subject:</strong> "GDPR Complaint - Data Protection"</p>
              </div>
            </div>
          </section>

          {/* Contact Information */}
          <section className="mb-8 p-6 bg-gray-50 rounded-lg">
            <h2 className="text-xl font-bold text-gray-900 mb-4">GDPR Contact Information</h2>
            <p className="text-gray-700 mb-4">
              For all GDPR-related inquiries, requests, or concerns regarding HomeFixer360:
            </p>
            <div className="space-y-2 text-gray-700">
              <p><strong>Data Protection Officer:</strong> Guy T. Dunn Sr.</p>
              <p><strong>Company:</strong> Idea Guy Brands Incorporated</p>
              <p><strong>Email:</strong> guy@ideaguybrands.com</p>
              <p><strong>Subject Line:</strong> "GDPR Request - [Request Type]"</p>
            </div>
            <p className="text-gray-600 text-sm mt-4">
              We take your privacy rights seriously and will handle all requests with appropriate care and attention.
            </p>
          </section>

          {/* Copyright Footer */}
          <div className="border-t pt-6 mt-8 text-center text-gray-500 text-sm">
            <p>© 2025 Guy T. Dunn Sr. / Idea Guy Brands Incorporated. All Rights Reserved.</p>
            <p>HomeFixer360™ is a trademark of Idea Guy Brands Incorporated.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GDPRCompliance;